<template>
  <e-dialog ref="dialogRef" v-model="isDialogOpen" @after-leave="handleDialogAfterLeave" @open="handleDialogOpen">
    <template #activator>
      <slot name="activator" />
    </template>

    <template #title>
      <div class="dialog-header">
        <div class="h3 mb-2">Просмотр</div>
        <reconciliation-status
          :status="localReconciliationData?.commonInfo?.orderReconciliationStatus as OrderReconciliationStatus"
        />
      </div>
    </template>

    <div class="d-flex flex-column row-gap-6">
      <e-data-table
        :items="tableData"
        :headers="headers"
        hide-default-header
        hide-default-footer
        items-per-page="-1"
        disable-pagination
        disable-sort
        disable-filter
        class="info-table-vertical e-data-table--vertical-align-top"
        vertical-align="top"
        content-wrap
      >
        <template v-slot:[`item.label`]="{ item }: { item: TableItem }">
          <div class="th ellipsis-1 table-item">
            {{ item.label }}
          </div>
        </template>

        <template v-slot:[`item.value`]="{ item: tableItem }">
          <template v-if="tableItem.label === '№ бронирования' && localReconciliationData">
            <router-link
              :to="{
                name: RouteNames.Book,
                params: { b_id: localReconciliationData.commonInfo.orderId },
              }"
              class="link td font-600 table-item"
            >
              {{ tableItem.value }}
            </router-link>
            <div v-if="tableItem.subValue" class="paragraph-s text-secondary mt-1">
              {{ tableItem.subValue }}
            </div>
          </template>

          <template v-else-if="tableItem.label === 'Гости'">
            <div class="td font-600 guest-names table-item">
              {{ tableItem.value }}
            </div>
          </template>

          <template v-else-if="tableItem.label === 'Аннулировано'">
            <cancelled-status :status="tableItem.componentProps?.status!" />
          </template>

          <template v-else-if="tableItem.label === 'Заезд–Выезд'">
            <div class="td font-600 table-item">
              {{ tableItem.value }}
            </div>
          </template>

          <template v-else>
            <div class="td font-600 table-item text-end">
              {{ tableItem.value }}
            </div>
          </template>
        </template>
      </e-data-table>

      <div v-if="canEditActions" class="d-flex align-center justify-space-between w-100">
        <toggle-with-description
          v-model="showReasons"
          label="Есть расхождения"
          class="reconciliation-toggle"
          :loading="isTogglingMismatch"
          :disabled="isPeriodReconciled"
        />

        <slot
          name="reconcile-button"
          :loading="isReconciling"
          :disabled="showReasons || isAlreadyReconciled"
          :on-click="handleReconcile"
          :can-edit="canEditActions"
        >
          <e-btn
            variant="primary"
            size="L"
            class="elevation-0"
            :loading="isReconciling"
            :disabled="showReasons || isAlreadyReconciled || !canEditActions"
            @click="handleReconcile"
          >
            Сверить
          </e-btn>
        </slot>
      </div>

      <div v-if="showReasons" class="toggle-content">
        <div class="reasons-content">
          <template v-if="!justSentMismatch">
            <div class="mb-4">
              <e-select
                v-model="localSelectedReason"
                label="Причина расхождения"
                class="reason-select"
                clearable
                :items="reasonItems"
                :disabled="isPeriodReconciled || !canEditActions"
              />
            </div>

            <div class="mb-6">
              <e-textarea
                v-model="description"
                label="Описание расхождений в бронировании"
                :rows="4"
                :maxlength="500"
                placeholder="Описание расхождений в бронировании"
                :disabled="isPeriodReconciled || !canEditActions"
              />
            </div>

            <div class="d-flex">
              <e-btn
                variant="primary"
                prepend-icon-src="messageSend"
                size="L"
                class="ml-auto"
                :loading="isSavingMismatchDetails"
                :disabled="isSaveButtonDisabled || !canEditActions"
                @click="handleSaveMismatchDetails"
              >
                Отправить
              </e-btn>
            </div>
          </template>

          <div v-else-if="justSentMismatch" class="message-sent">
            <div class="d-flex flex-column align-center">
              <img :src="imgMessageOk" width="120" height="120" alt="Успешно отправлено" class="mb-6" />

              <div class="text-center">
                <div class="caption">Ваше обращение успешно отправлено!</div>
                <p class="paragraph-m_h text-secondary mt-2">Наши менеджеры свяжутся с вами в ближайшее время.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </e-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CancelledStatus from '../components/cancelled-status.vue';
import ReconciliationStatus from '../components/reconciliation-status.vue';
import type { OrderReconciliationView } from '@/graphql/generated';
import imgMessageOk from '@/assets/img/pages/support/ok.svg';
import ToggleWithDescription from '@/core/components/common/DescriptionToggle.vue';
import EDataTable from '@/core/components/common/data-items/EDataTable.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ESelect, ETextarea } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getDatePeriod } from '@/core/utils/date';
import { formatPrice } from '@/core/utils/number';
import {
  ContractReconciliationStates,
  OrderAvoidanceStatus,
  OrderReconciliationStatus,
  MismatchReason,
  useMismatchOrderReconciliationMutation,
  useReconcileOrdersReconciliationMutation
} from '@/graphql/generated';
import { isOperationVisible } from '@/core/utils/permissions';

interface TableItem extends Record<string, unknown> {
  label: string;
  value?: string | number;
  subValue?: string;
  align?: 'right' | 'left' | 'center';
  componentProps?: {
    status?: OrderAvoidanceStatus;
    canEdit?: boolean;
  };
}

const props = defineProps<{
  reconciliationData?: OrderReconciliationView | null;
  periodStatus?: ContractReconciliationStates;
  canEdit?: boolean;
  mismatchFromMenu?: boolean;
}>();

const emit = defineEmits<{
  (event: 'after-leave'): void;
  (event: 'reconcile'): void;
  (event: 'mismatch', payload: { 
    orderId: number; 
    reason: MismatchReason; 
    description: string;
    status?: OrderReconciliationStatus;
  }): void;
  (event: 'status-changed', payload: { orderId: number; status: OrderReconciliationStatus }): void;
}>();

const isDialogOpen = defineModel<boolean>('modelValue');

const dialogRef = ref();
const localSelectedReason = ref<MismatchReason | null>(null);
const description = ref('');
const isReconciling = ref(false);
const isTogglingMismatch = ref(false);
const isSavingMismatchDetails = ref(false);
const originalMismatchData = ref<{
  reason: string | null;
  description: string;
} | null>(null);
const justSentMismatch = ref(false);
const wasJustSent = ref(false);

const isAlreadyReconciled = computed(
  () => localReconciliationData.value?.commonInfo?.orderReconciliationStatus === OrderReconciliationStatus.Reconciled
    || localReconciliationData.value?.commonInfo?.orderReconciliationStatus === OrderReconciliationStatus.ReconciledByAlean,
);

const itemOperations = computed(() => localReconciliationData.value?.operations);

const canEditActions = computed(() => 
  props.canEdit && isOperationVisible(itemOperations.value?.edit)
);

const localReconciliationData = ref<OrderReconciliationView | null>(null);

const showReasons = ref(false);

const isPeriodReconciled = computed(
  () => props.periodStatus === ContractReconciliationStates.Reconciled
);

const initializeDialogData = () => {
  if (!props.reconciliationData) {
    if (!wasJustSent.value && localReconciliationData.value !== null) {
      resetDialogState();
    }
    return;
  }

  localReconciliationData.value = { ...props.reconciliationData };

  const isMismatchStatus =
    localReconciliationData.value.commonInfo.orderReconciliationStatus === OrderReconciliationStatus.Mismatch;

  showReasons.value = props.mismatchFromMenu || isMismatchStatus;

  if (localReconciliationData.value.mismatch) {
    localSelectedReason.value = localReconciliationData.value.mismatch.reason;
    description.value = localReconciliationData.value.mismatch.description || '';

    originalMismatchData.value = {
      reason: localReconciliationData.value.mismatch.reason,
      description: localReconciliationData.value.mismatch.description || '',
    };
  } else {
    localSelectedReason.value = null;
    description.value = '';
    originalMismatchData.value = null;
  }

  justSentMismatch.value = false;
  wasJustSent.value = false;
};

const resetDialogState = () => {
  localReconciliationData.value = null;
  showReasons.value = false;
  localSelectedReason.value = null;
  description.value = '';
  originalMismatchData.value = null;
  justSentMismatch.value = false;
  wasJustSent.value = false;
  isReconciling.value = false;
  isTogglingMismatch.value = false;
  isSavingMismatchDetails.value = false;
};

watch(
  () => props.reconciliationData,
  (newData) => {
    if (wasJustSent.value) {
      return; 
    }

    if (newData === null) {
      resetDialogState();
    } else {
      initializeDialogData();
    }
  },
  { deep: true },
);

watch(
  () => props.mismatchFromMenu,
  (newValue) => {
    if (newValue && localReconciliationData.value) {
      showReasons.value = true;
      
      const isMismatchStatus = 
        localReconciliationData.value.commonInfo.orderReconciliationStatus === OrderReconciliationStatus.Mismatch;
      
      if (!isMismatchStatus) {
        localSelectedReason.value = null;
        description.value = '';
        originalMismatchData.value = null;
      }
    }
  },
  { immediate: true }
);

const reasonItems = [
  {
    title: 'Досрочный выезд',
    value: MismatchReason.EarlyCheckout,
  },
  {
    title: 'Не заезд',
    value: MismatchReason.NoShow,
  },
  {
    title: 'Некорректный тариф',
    value: MismatchReason.IncorrectRate,
  },
  {
    title: 'Другое',
    value: MismatchReason.Other,
  },
];

const headers = [
  { key: 'label', title: '', width: '320px', sortable: false },
  { key: 'value', title: '', sortable: false },
];

const tableData = computed<TableItem[]>(() => {
  if (!localReconciliationData.value) return [];

  const data = localReconciliationData.value.commonInfo;

  return [
    {
      label: '№ бронирования',
      value: data.reconciliationItemTitle,
      subValue: data.reconciliationItemSubTitle,
    },
    {
      label: 'Гости',
      value: data.tourists.map((t) => t.name.toUpperCase()).join('; '),
    },
    {
      label: 'Заезд–Выезд',
      value: getDatePeriod(data.period.from, data.period.to, true),
    },
    {
      label: 'Аннулировано',
      componentProps: { status: data.orderAvoidanceStatus },
    },
    {
      label: 'Выплаты',
      value: formatPrice(data.amountsInfo.payout),
      align: 'right',
    },
    {
      label: 'К оплате',
      value: formatPrice(data.amountsInfo.payable),
      align: 'right',
    },
  ];
});

const isMismatchFormValid = computed(
  () => localSelectedReason.value !== null && localSelectedReason.value.trim() !== '',
);

const hasMismatchDataChanged = computed(() => {
  if (!originalMismatchData.value) {
    return localSelectedReason.value !== null || description.value.trim() !== '';
  }

  const currentReason = localSelectedReason.value;
  const currentDescription = description.value || '';

  if (
    (!currentReason && originalMismatchData.value.reason) ||
    (!currentDescription && originalMismatchData.value.description)
  ) {
    return true;
  }

  const reasonChanged = currentReason !== originalMismatchData.value.reason;
  const descriptionChanged = currentDescription !== originalMismatchData.value.description;

  return reasonChanged || descriptionChanged;
});

const isSaveButtonDisabled = computed(
  () => !isMismatchFormValid.value || (originalMismatchData.value !== null && !hasMismatchDataChanged.value) || isPeriodReconciled.value,
);

const handleSaveMismatchDetails = async () => {
  if (isPeriodReconciled.value) {
    return;
  }

  if (!localReconciliationData.value || !localSelectedReason.value) {
    return;
  }

  isSavingMismatchDetails.value = true;
  wasJustSent.value = true; 

  const orderId = localReconciliationData.value.commonInfo.orderId;
  const reason = localSelectedReason.value;
  const desc = description.value || '';

  const { request } = useMismatchOrderReconciliationMutation({
    input: {
      orderId,
      mismatch: { reason, description: desc },
    },
  });

  const result = await request();

  justSentMismatch.value = true;

  if (result?.data?.mismatchOrderReconciliation?.orderReconciliation) {
    const data = result.data.mismatchOrderReconciliation.orderReconciliation;
    
    if (localReconciliationData.value) {
      localReconciliationData.value.commonInfo.orderReconciliationStatus = data.commonInfo.orderReconciliationStatus;
      
      if (data.mismatch) {
        localReconciliationData.value.mismatch = data.mismatch;
        localSelectedReason.value = data.mismatch.reason;
        description.value = data.mismatch.description || '';

        originalMismatchData.value = {
          reason: data.mismatch.reason,
          description: data.mismatch.description || '',
        };
      }
    }
  }

  emit('mismatch', {
    orderId,
    reason,
    description: desc,
    status: OrderReconciliationStatus.Mismatch
  });

  isSavingMismatchDetails.value = false;
};

const handleReconcile = async () => {
  if (!props.reconciliationData || showReasons.value || isAlreadyReconciled.value || isPeriodReconciled.value) {
    return;
  }

  isReconciling.value = true;

  const { request } = useReconcileOrdersReconciliationMutation({
    input: { orderIds: [props.reconciliationData.commonInfo.orderId] },
  });

  await request();
  emit('reconcile');
  isDialogOpen.value = false;
  isReconciling.value = false;
};

const handleDialogOpen = () => {
  justSentMismatch.value = false;
  wasJustSent.value = false;
  initializeDialogData();
};

const handleDialogAfterLeave = () => {
  emit('after-leave');
  resetDialogState();
  wasJustSent.value = false; 
};

const open = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const close = () => {
  if (dialogRef.value) {
    dialogRef.value.close();
  }
};

defineExpose({
  open,
  close,
  getSelectedReason: () => localSelectedReason.value,
  getDescription: () => description.value,
  handleReconcile,
  handleSaveMismatchDetails,
});
</script>

<style scoped lang="scss">
.info-table-vertical {
  --v-table-row-height: auto;
  --v-table-header-height: auto;
}

.table-item {
  font-size: $paragraph-s-font-size;
  line-height: $paragraph-m-line-height;
}

.info-table-vertical :deep(.th.table-item) {
  font-weight: 400;
  color: var(--color-text-secondary);
}
</style>
